if (!customElements.get("pagination-load-more")) {
    class PaginationLoadMore extends HTMLElement {
        constructor() {
            super();

            this.loadMoreBtn = this.querySelector(".pagination-load-more");
            this.paginationType = this.dataset.paginationType;

            this.isLoading = false;
            this.loadedUrls = new Set();

            this.handleClick = this.handleClick.bind(this);
            this.handleIntersection = this.handleIntersection.bind(this);
        }

        connectedCallback() {
            if (!this.loadMoreBtn) return;

            // מסמנים את העמוד הנוכחי כדי שלא ייטען שוב
            this.loadedUrls.add(this.normalizeUrl(window.location.href));

            if (this.paginationType === "infinity_scroll") {
                this.infinityScroll();
            } else {
                this.loadMoreBtn.addEventListener("click", this.handleClick);
            }
        }

        handleClick(event) {
            event.preventDefault();
            this.loadNextPage();
        }

        handleIntersection(entries) {
            entries.forEach((entry) => {
                if (entry.isIntersecting && !this.isLoading) {
                    this.loadNextPage();
                }
            });
        }

        infinityScroll() {
            if (!this.loadMoreBtn) return;

            this.observer = new IntersectionObserver(
                this.handleIntersection,
                {
                    root: null,
                    rootMargin: "0px 0px 300px 0px",
                    threshold: 0,
                }
            );

            this.observer.observe(this.loadMoreBtn);
        }

        async loadNextPage() {
            if (!this.loadMoreBtn || this.isLoading) return;

            const requestedUrl = this.normalizeUrl(this.getPageUrl());

            // אם הכתובת כבר נטענה, עוצרים את הגלילה
            if (!requestedUrl || this.loadedUrls.has(requestedUrl)) {
                this.finishPagination();
                return;
            }

            this.isLoading = true;
            this.loadedUrls.add(requestedUrl);
            this.showLoader();

            try {
                const response = await fetch(requestedUrl, {
                    headers: {
                        "X-Requested-With": "XMLHttpRequest",
                    },
                });

                if (!response.ok) {
                    throw new Error(
                        `Pagination request failed: ${response.status}`
                    );
                }

                const text = await response.text();

                const dataHtml = new DOMParser().parseFromString(
                    text,
                    "text/html"
                );

                const sources = [
                    "[data-product-id]",
                    "[data-collection-id]",
                    "[data-article-id]",
                ];

                let appendedCount = 0;

                sources.forEach((source) => {
                    let items = Array.from(
                        dataHtml.querySelectorAll(
                            `[data-list-items] ${source}`
                        )
                    );

                    if (!items.length) return;

                    // סינון לפי מלאי
                    if (requestedUrl.includes("availability=1")) {
                        items = items.filter(
                            (item) => !item.classList.contains("sold-out")
                        );
                    } else if (requestedUrl.includes("availability=0")) {
                        items = items.filter((item) =>
                            item.classList.contains("sold-out")
                        );
                    }

                    appendedCount += this.appendItems(items);
                });

                const nextButton = dataHtml.querySelector(
                    ".pagination-load-more"
                );

                const nextUrl = nextButton?.href
                    ? this.normalizeUrl(nextButton.href)
                    : null;

                const requestedPage = this.getPageNumber(requestedUrl);
                const nextPage = this.getPageNumber(nextUrl);

                const nextUrlIsValid =
                    nextUrl &&
                    nextUrl !== requestedUrl &&
                    !this.loadedUrls.has(nextUrl) &&
                    (
                        requestedPage === null ||
                        nextPage === null ||
                        nextPage > requestedPage
                    );

                if (nextUrlIsValid) {
                    this.loadMoreBtn.href = nextUrl;

                    // נעדכן את הכתובת בלי ליצור המון רשומות בהיסטוריה
                    history.replaceState(null, "", requestedUrl);
                } else {
                    this.finishPagination();
                }

                /*
                 * אם לא נוסף אפילו מוצר אחד והעמוד הבא כבר לא תקין,
                 * pagination ייעצר ולא יחזור שוב להתחלה.
                 */
                if (appendedCount === 0 && !nextUrlIsValid) {
                    this.finishPagination();
                }
            } catch (error) {
                console.error("Infinity scroll error:", error);

                // מאפשרים ניסיון נוסף במקרה של שגיאת רשת
                this.loadedUrls.delete(requestedUrl);
            } finally {
                this.isLoading = false;
                this.hideLoader();
            }
        }

        getPageUrl() {
            if (!this.loadMoreBtn?.href) return null;

            const url = new URL(
                this.loadMoreBtn.href,
                window.location.origin
            );

            const filtersForm = document.querySelector(
                "collection-filters-form"
            );

            if (filtersForm?.form) {
                const formData = new FormData(filtersForm.form);
                const filterParams = new URLSearchParams(formData);

                filterParams.forEach((value, key) => {
                    url.searchParams.set(key, value);
                });
            }

            return url.href;
        }

        appendItems(items) {
            const listItems = document.querySelector("[data-list-items]");
            if (!listItems) return 0;

            let appendedCount = 0;

            items.forEach((item) => {
                if (
                    item.nodeName !== "LI" &&
                    item.nodeName !== "ARTICLE"
                ) {
                    return;
                }

                const uniqueSelector = this.getItemSelector(item);

                // המוצר כבר נמצא בעמוד, לא מוסיפים אותו שוב
                if (
                    uniqueSelector &&
                    listItems.querySelector(uniqueSelector)
                ) {
                    return;
                }

                listItems.appendChild(item);
                appendedCount++;
            });

            if (appendedCount > 0) {
                this.updateRevealAnimation();

                if (
                    typeof window.renderPrivatePriceInScope === "function"
                ) {
                    window.renderPrivatePriceInScope(listItems);
                }
            }

            return appendedCount;
        }

        getItemSelector(item) {
            if (item.dataset.productId) {
                return `[data-product-id="${CSS.escape(
                    item.dataset.productId
                )}"]`;
            }

            if (item.dataset.collectionId) {
                return `[data-collection-id="${CSS.escape(
                    item.dataset.collectionId
                )}"]`;
            }

            if (item.dataset.articleId) {
                return `[data-article-id="${CSS.escape(
                    item.dataset.articleId
                )}"]`;
            }

            return null;
        }

        getPageNumber(url) {
            if (!url) return null;

            try {
                const page = new URL(
                    url,
                    window.location.origin
                ).searchParams.get("page");

                if (!page) return null;

                const parsedPage = Number(page);

                return Number.isFinite(parsedPage)
                    ? parsedPage
                    : null;
            } catch {
                return null;
            }
        }

        normalizeUrl(url) {
            if (!url) return null;

            try {
                const normalizedUrl = new URL(
                    url,
                    window.location.origin
                );

                normalizedUrl.hash = "";

                // מסדר את הפרמטרים כדי ששתי כתובות זהות יזוהו כזהות
                normalizedUrl.searchParams.sort();

                return normalizedUrl.href;
            } catch {
                return null;
            }
        }

        finishPagination() {
            if (!this.loadMoreBtn) return;

            this.observer?.unobserve(this.loadMoreBtn);
            this.observer?.disconnect();

            this.loadMoreBtn.removeEventListener(
                "click",
                this.handleClick
            );

            this.loadMoreBtn.remove();
            this.loadMoreBtn = null;
        }

        async updateRevealAnimation() {
            const listItems = document.querySelector("[data-list-items]");
            const revealList =
                listItems?.closest(".reveal-slide-in");

            if (!revealList || typeof onReveal !== "function") return;

            const revealListArr = [listItems];

            await onReveal(revealListArr);

            if (typeof updateItemZIndex === "function") {
                await updateItemZIndex(revealListArr);
            }
        }

        showLoader() {
            let loader = document.querySelector(
                ".pagination-page-loader"
            );

            if (!loader) {
                loader = document.createElement("div");
                loader.className = "pagination-page-loader";

                loader.innerHTML = `
                    <div class="pagination-page-loader__spinner">
                        <svg
                            class="spinner"
                            width="48"
                            height="48"
                            viewBox="0 0 50 50"
                            aria-hidden="true"
                        >
                            <circle
                                class="path"
                                cx="25"
                                cy="25"
                                r="20"
                                fill="none"
                                stroke-width="4"
                            ></circle>
                        </svg>
                    </div>
                `;

                document.body.appendChild(loader);
            }

            loader.classList.add("is-visible");
            document.body.classList.add("pagination-is-loading");

            this.loadMoreBtn?.classList.add("loading");
            this.loadMoreBtn?.setAttribute("aria-busy", "true");
        }

        hideLoader() {
            const loader = document.querySelector(
                ".pagination-page-loader"
            );

            loader?.classList.remove("is-visible");
            document.body.classList.remove("pagination-is-loading");

            this.loadMoreBtn?.classList.remove("loading");
            this.loadMoreBtn?.removeAttribute("aria-busy");
        }

        disconnectedCallback() {
            this.observer?.disconnect();

            this.loadMoreBtn?.removeEventListener(
                "click",
                this.handleClick
            );

            this.hideLoader();
        }
    }

    customElements.define(
        "pagination-load-more",
        PaginationLoadMore
    );
}